import { Colors, DATE_FORMATS, formatDate, Icon, Pills } from '@amelio/ui-library';

import type { SkillCategory } from '@/types/performance-profile';
import type { Ref } from 'react';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { ProficiencyLevelBars } from '@/components/ProficiencyIcon/ProficiencyLevelBars';
import { resolveProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { Locale } from '@/constants/Locale';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';

interface LegendEntry {
    id: string;
    label: string;
    iconName?: string;
    iconUrl?: string;
    iconColor?: string;
}

export interface SkillsetAssessmentPdfDocumentProps {
    employeeName: string;
    competencyProfileName?: string | null;
    generatedAt: Date;
    categories: SkillCategory[];
    /** The scale in reading order, worst level first, as the on-screen legend shows it. */
    legend: LegendEntry[];
    /** The root of the document, for the capture that turns it into a PDF. */
    ref?: Ref<HTMLDivElement>;
}

// Print-only layout used to render the skillset assessment before it is captured into a PDF on
// the client. It intentionally mirrors the look of the previous server-generated PDF (teal
// header, etc.) but reuses the same ProficiencyIcon/getIconEntry pieces and level pills
// as the on-screen card, so the icon rendered for a given proficiency level always matches what
// the user sees on the page (bug 13976-adjacent icon mismatch).
export const SkillsetAssessmentPdfDocument = ({
    employeeName,
    competencyProfileName,
    generatedAt,
    categories,
    legend,
    ref,
}: SkillsetAssessmentPdfDocumentProps) => {
    const lang = i18n.language === Locale.EN ? 'en' : 'fr';
    // The legend already carries each level's colour, so a category's pill reads it there rather
    // than from a second copy of the scale.
    const levelColors = new Map(legend.map((entry) => [entry.id, entry.iconColor]));

    const renderIcon = (
        iconName: string | undefined,
        iconUrl: string | undefined,
        iconColor: string | undefined,
    ) =>
        iconName ? (
            <ProficiencyIcon
                iconUrl={iconUrl}
                iconEntry={iconName ? getIconEntry(iconName) : undefined}
                color={iconColor}
                className={'h-4 w-4 shrink-0'}
            />
        ) : (
            <Icon
                name={'essential-minus'}
                color={Colors.BASE_400}
                size={'lg'}
            />
        );

    // A skill that has left the profile is still printed, struck through and labelled: the reader
    // can otherwise not tell it from one that was simply not assessed.
    const skillStateKey = (skill: SkillCategory['skills'][number]) => {
        if (skill.isDeleted) {
            return 'common.deleted';
        }
        if (skill.isDeactivated) {
            return 'common.deactivated';
        }
        return null;
    };

    const renderSkillProficiency = (
        skillId: string,
        skillProficiency: SkillCategory['skills'][number]['proficiency'],
    ) => {
        if (!skillProficiency) {
            return renderIcon(undefined, undefined, undefined);
        }

        // The bars count from the best level, the far end of the reading order.
        const position = legend.findIndex((entry) => entry.id === skillProficiency.id);
        const levelIndex = position < 0 ? position : legend.length - 1 - position;
        // The legend is the one place a level's colour is decided, so the printed row reads it
        // there first; the assessment's own snapshot only answers for a level the legend dropped.
        const levelColor = resolveProficiencyLevelColor({
            iconColor: levelColors.get(skillProficiency.id) ?? skillProficiency.iconColor,
            levelIndex,
        });

        return (
            <div className={'flex shrink-0 flex-col items-center gap-1'}>
                {renderIcon(skillProficiency.icon, skillProficiency.iconUrl, levelColor)}
                <ProficiencyLevelBars
                    levelIndex={levelIndex}
                    totalLevels={legend.length}
                    color={levelColor}
                    dataTestId={`skillset-pdf-skill-${skillId}-proficiency-bars`}
                />
            </div>
        );
    };

    return (
        <div
            ref={ref}
            className={'text-base-900 w-[794px] bg-white'}
            style={{ fontFamily: 'Arial, sans-serif' }}
            data-skillset-pdf-ref={'true'}
        >
            <div className={'bg-primary-500 p-7'}>
                <h1 className={'text-heading font-bold text-white'}>
                    {i18n.t('performanceProfile.skillsetAssessment.title')}
                </h1>
                <div className={'mt-1 text-sm text-white'}>
                    {employeeName}
                    {' — '}
                    {formatDate({
                        date: generatedAt.toISOString(),
                        format: DATE_FORMATS.ISO_DATE,
                        language: lang,
                    })}
                </div>
            </div>

            <div className={'p-7'}>
                <div className={'text-base font-bold'}>{employeeName}</div>
                <div
                    className={'text-base-500 mt-0.5 mb-4 text-sm'}
                    style={{ minHeight: competencyProfileName ? undefined : '16px' }}
                >
                    {competencyProfileName
                        ? `${i18n.t('performanceProfile.competencyProfile.title')} : ${competencyProfileName}`
                        : ''}
                </div>

                <div className={'flex flex-col gap-2.5'}>
                    {categories.map((category) => (
                        <div
                            key={category.id}
                            className={'border-base-100 overflow-hidden rounded-md border'}
                            data-pdf-block={'true'}
                        >
                            <div
                                className={
                                    'bg-secondary-25 flex items-center justify-between px-3 py-3'
                                }
                            >
                                <span className={'text-sm font-bold'}>{category.name}</span>
                                {category.proficiency && (
                                    <Pills
                                        variant={'tinted'}
                                        color={levelColors.get(category.proficiency.id)}
                                        size={'sm'}
                                    >
                                        {getLocalizedText(category.proficiency.label)}
                                    </Pills>
                                )}
                            </div>

                            {category.skills.map((skill) => {
                                const stateKey = skillStateKey(skill);
                                return (
                                    <div
                                        key={skill.id}
                                        className={
                                            'border-base-100 flex items-center gap-2 border-t px-4 py-2.5'
                                        }
                                    >
                                        {renderSkillProficiency(skill.id, skill.proficiency)}
                                        <span
                                            className={`text-sm ${stateKey ? 'line-through' : ''}`}
                                        >
                                            {skill.name}
                                        </span>
                                        {stateKey && <Pills size={'xs'}>{i18n.t(stateKey)}</Pills>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {legend.length > 0 && (
                    <>
                        <div className={'border-base-100 mt-4 border-t pt-2.5'} />
                        <div className={'flex flex-wrap gap-4'}>
                            {legend.map((level) => (
                                <span
                                    key={level.id}
                                    className={'flex items-center gap-1'}
                                >
                                    {renderIcon(level.iconName, level.iconUrl, level.iconColor)}
                                    <span className={'text-base-600 text-xs'}>{level.label}</span>
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
